/**
 * React Error Boundary component.
 *
 * Catches JavaScript errors in the component tree, logs them, and displays a
 * fallback UI instead of crashing the entire application.
 */

'use client'

import React, { Component, ReactNode } from 'react'
import { datadogRum } from '@datadog/browser-rum'
import { ErrorPage } from './ErrorPage'
import { logger } from '@/lib/services/logger.service'

/** Props for the ErrorBoundary component. */
export interface ErrorBoundaryProps {
  /** Child components to wrap */
  children: ReactNode
  /** Custom fallback UI (optional) */
  fallback?: ReactNode
  /** Callback when error is caught (optional) */
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void
}

/** State for the ErrorBoundary component. */
interface ErrorBoundaryState {
  /** Whether an error has been caught */
  hasError: boolean
  /** The caught error (if any) */
  error: Error | null
}

/**
 * ErrorBoundary component for catching and handling React errors.
 *
 * Wraps components to catch errors in their render methods, lifecycle methods,
 * and constructors. Displays a fallback UI and prevents the entire app from
 * crashing.
 */
export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
    }
  }

  /**
   * Static method called when an error is caught.
   *
   * Updates state to trigger fallback UI rendering.
   */
  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
    }
  }

  /**
   * Lifecycle method called after an error is caught.
   *
   * Logs the error and calls the optional onError callback.
   */
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    logger.error('Error Boundary caught error', error, {
      source: 'react-error-boundary',
      componentStack: errorInfo.componentStack,
    })

    try {
      datadogRum.addError(error, {
        source: 'react-error-boundary',
        componentStack: errorInfo.componentStack,
      })
    } catch {
      // RUM may not be initialized; logger.error already captured the event
    }

    if (this.props.onError) {
      this.props.onError(error, errorInfo)
    }
  }

  /**
   * Resets the error boundary state.
   *
   * Allows users to attempt recovery by re-rendering the component tree.
   */
  private resetError = (): void => {
    this.setState({
      hasError: false,
      error: null,
    })
  }

  render(): ReactNode {
    // If error caught, render fallback UI
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback
      }

      // Default fallback UI
      return (
        <ErrorPage
          title="Something went wrong"
          message="We encountered an error in this component. Please try again or return home."
          code={this.state.error?.name}
          actions={[
            {
              label: 'Try again',
              icon: 'refresh',
              onClick: this.resetError,
              testId: 'btn_tryAgain',
            },
            {
              label: 'Go home',
              icon: 'forward',
              href: '/',
              testId: 'btn_goHome',
            },
          ]}
        />
      )
    }

    // No error, render children normally
    return this.props.children
  }
}
